import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import SideBar from "../other/SideBar";
import NavBar from "../other/NavBar";
import LoadingModal from "../../loader/LoadingModal";
import Notification from "../other/Notification";
import { GetUserDetailsURL } from "../../assets/api/BaseURL";
import { useToast } from "../../../context/ToastContext";
import "./support.css";

const Support = () => {
  const navigate = useNavigate();
  const { addToast, planData, setPlan } = useToast();
  const [token, setToken] = useState("");
  const [toggleSideBar, setToggleSideBar] = useState(false);
  const [canvas, setCanvas] = useState(false);
  const [isLoading] = useState(false);
  const [accountUser, setAccountUser] = useState(null);

  const capitalizeWords = useCallback((str) => {
    if (!str || typeof str !== "string") return "";
    return str
      .split(" ")
      .map((word) =>
        word.length === 0 ? "" : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      )
      .join(" ");
  }, []);

  const handleToggleSidebar = (e) => {
    e.preventDefault();
    setToggleSideBar(!toggleSideBar);
  };

  const handleToggleOffCanvas = (e) => {
    e.preventDefault();
    setCanvas(!canvas);
  };

  useEffect(() => {
    if (!localStorage.getItem("token")) {
      navigate("/auth");
    }
  }, [navigate]);

  useEffect(() => {
    setToken(localStorage.getItem("token"));
  }, [token]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(GetUserDetailsURL, {
          headers: { authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (response.status === 200 && response.data?.data) {
          const data = response.data.data;
          setAccountUser(data);
          if (data.planDetailsResult) {
            setPlan(data.planDetailsResult);
          }
        }
      } catch (error) {
        if (!cancelled) {
          if (error.response?.data?.message) {
            addToast("error", error.response.data.message);
          } else {
            addToast("error", error.message);
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]); /* fetch account when session token is set; addToast/setPlan omitted to avoid context churn re-runs */

  const copyRestaurantId = useCallback(async () => {
    const id = accountUser?.fooder_uniqueid;
    if (id === undefined || id === null || String(id).trim() === "") {
      return;
    }
    const text = String(id).trim();
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      addToast("success", "Copied to clipboard");
    } catch {
      addToast("error", "Could not copy to clipboard");
    }
  }, [accountUser, addToast]);

  const planName = planData?.plan_name ?? "";

  return (
    <>
      {token && (
        <>
          <div
            className={
              toggleSideBar
                ? "container-scroller sidebar-icon-only support-layout"
                : "container-scroller support-layout"
            }
          >
            <nav className="navbar default-layout col-lg-12 col-12 p-0 fixed-top d-flex align-items-top flex-row">
              <div className="text-center navbar-brand-wrapper d-flex align-items-center justify-content-start">
                <Link className="navbar-brand brand-logo-mini" to="/fooders_cp">
                </Link>
                <div className="me-1">
                  <button
                    className={
                      toggleSideBar
                        ? "navbar-toggler navbar-toggler align-self-center"
                        : "navbar-toggler navbar-toggler align-self-center text-white"
                    }
                    type="button"
                    onClick={handleToggleSidebar}
                  >
                    <span className="icon-menu"></span>
                  </button>
                </div>
                <div>
                  <Link to="/fooders_cp">
                    <span className="navbar-brand brand-logo">
                      <img src="images/vyfoo_logo.png" alt="Vyfoo" />
                    </span>
                  </Link>
                </div>
              </div>
              <NavBar handleToggle={handleToggleOffCanvas} toggleCanvas={toggleSideBar} />
            </nav>
            <div className="container-fluid page-body-wrapper px-0">
              <SideBar active="support" toggleCanvas={toggleSideBar} />

              <div className="main-panel">
                <div className="content-wrapper">
                  <div className="row">
                    <div className="col-sm-12">
                      <h3 className="ukhd mb-3">Support</h3>

                      <div className="card card-rounded support-account-card mb-4">
                        <div className="support-card-top" aria-hidden="true" />
                        <div className="card-body">
                          <h4 className="card-title card-title-dash mb-3">
                            <i
                              className="mdi mdi-card-account-details-outline me-2"
                              style={{ color: "#2dc176" }}
                              aria-hidden="true"
                            />
                            Account information
                          </h4>
                          <p className="text-muted small mb-3">
                            Please include your Restaurant ID when contacting support.
                          </p>
                          <div className="row g-3 support-account-grid">
                            <div className="col-sm-6 col-lg-3">
                              <div className="support-account-label">Restaurant name</div>
                              <div className="support-account-value text-break">
                                {accountUser?.contact_name
                                  ? capitalizeWords(accountUser.contact_name)
                                  : "—"}
                              </div>
                            </div>
                            <div className="col-sm-6 col-lg-3">
                              <div className="support-account-label">Restaurant ID</div>
                              <div className="d-flex flex-wrap align-items-center gap-2">
                                <span className="support-account-value text-break font-monospace">
                                  {accountUser?.fooder_uniqueid != null &&
                                    String(accountUser.fooder_uniqueid).trim() !== ""
                                    ? String(accountUser.fooder_uniqueid).trim()
                                    : "—"}
                                </span>
                                {accountUser?.fooder_uniqueid != null &&
                                  String(accountUser.fooder_uniqueid).trim() !== "" && (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-secondary support-copy-id-btn"
                                      onClick={copyRestaurantId}
                                    >
                                      Copy
                                    </button>
                                  )}
                              </div>
                            </div>
                            <div className="col-sm-6 col-lg-3">
                              <div className="support-account-label">Email</div>
                              <div className="support-account-value text-break">
                                {accountUser?.login_email?.trim()
                                  ? accountUser.login_email.trim()
                                  : "—"}
                              </div>
                            </div>
                            <div className="col-sm-6 col-lg-3">
                              <div className="support-account-label">Current plan</div>
                              <div className="support-account-value text-break">
                                {planName ? planName : "—"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="support-hero">
                        <span className="support-hero-badge">Global Support Team</span>
                        <h2>How can we help?</h2>
                        <p>
                          We serve restaurants and outlets worldwide. Our support team serves
                          customers worldwide across multiple time zones—reach us by email anytime, or
                          by phone during IST business hours.
                        </p>
                      </div>

                      <div className="row">
                        <div className="col-lg-6 grid-margin stretch-card">
                          <div className="card card-rounded h-100 support-card-accent">
                            <div className="support-card-top" aria-hidden="true" />
                            <div className="card-body">
                              <h4 className="card-title card-title-dash mb-3">
                                <i className="mdi mdi-headset me-2" style={{ color: "#2dc176" }} />
                                Contact us
                              </h4>
                              <div className="support-contact-highlight">
                                <strong className="d-block mb-1">Email</strong>
                                <a href="mailto:contact@vyfoo.com">contact@vyfoo.com</a>
                                <p className="text-muted small mb-0 mt-2">
                                  24/7 email support for billing, account access, and technical assistance.
                                  We reply in English, but you may write in another language if you prefer.
                                </p>
                              </div>
                              <div className="support-contact-highlight">
                                <strong className="d-block mb-1">Support Phone</strong>
                                <a href="tel:+917217215635">+91 72172 15635</a>
                                <p className="text-muted small mb-0 mt-2">
                                  Save or dial as <strong>+91 72172 15635</strong> (same as{" "}
                                  <strong>+917217215635</strong> in your contacts). If you call from
                                  outside India, your carrier may charge international rates.
                                </p>
                              </div>
                              <div className="support-hours mt-3">
                                <strong className="text-dark d-block mb-2">Support Hours</strong>
                                <p className="small mb-1">Mon–Sat</p>
                                <p className="mb-2 small">10:00 AM – 7:00 PM IST (UTC+5:30)</p>
                                <p className="text-muted small mb-0">
                                  Email support is available anytime.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-6 grid-margin stretch-card">
                          <div className="card card-rounded h-100 support-card-accent">
                            <div className="support-card-top" aria-hidden="true" />
                            <div className="card-body">
                              <h4 className="card-title card-title-dash mb-3">
                                <i className="mdi mdi-file-document me-2" style={{ color: "#2dc176" }} />
                                Help &amp; learning
                              </h4>
                              <div className="support-link-list">
                                <a
                                  href="https://vyfoo.com"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <i className="mdi mdi-open-in-new" />
                                  Vyfoo website &amp; overview
                                </a>
                                <Link to="/updates">
                                  <i className="mdi mdi-pencil-box" />
                                  Release notes &amp; what&apos;s new
                                </Link>
                                <Link to="/account_management">
                                  <i className="mdi mdi-account-circle-outline" />
                                  Account settings
                                </Link>
                              </div>
                              <p className="text-muted small mb-0 mt-3 ps-1">
                                <i className="mdi mdi-information-outline me-1" style={{ color: "#2dc176" }} />
                                Help center / video library links can be added here when ready for all
                                regions.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-12 grid-margin stretch-card">
                          <div className="card card-rounded support-card-accent">
                            <div className="support-card-top" aria-hidden="true" />
                            <div className="card-body">
                              <h4 className="card-title card-title-dash mb-4">
                                <i className="mdi mdi-help-circle-outline me-2" style={{ color: "#2dc176" }} />
                                Common topics
                              </h4>
                              <div className="row g-3">
                                <div className="col-md-6">
                                  <div className="support-topic-tile">
                                    <h6>
                                      <i className="mdi mdi-ticket-percent" />
                                      Taxes &amp; Compliance
                                    </h6>
                                    <p className="text-muted small mb-0">
                                      Configure taxes for your country in{" "}
                                      <Link to="/fooders_cp">Business Settings</Link>. Confirm rules with your advisor or local authority, as requirements vary by country.
                                    </p>
                                  </div>
                                </div>
                                <div className="col-md-6">
                                  <div className="support-topic-tile">
                                    <h6>
                                      <i className="mdi mdi-wallet-outline" />
                                      Tips &amp; Service Charges
                                    </h6>
                                    <p className="text-muted small mb-0">
                                      Follow your business policy and local labor and payment regulations. Card networks and payment processors may apply additional rules.
                                    </p>
                                  </div>
                                </div>
                                <div className="col-md-6">
                                  <div className="support-topic-tile">
                                    <h6>
                                      <i className="mdi mdi-account-group" />
                                      Staff &amp; POS Access
                                    </h6>
                                    <p className="text-muted small mb-0">
                                      Roles and tables under{" "}
                                      <Link to="/view_staff">Staff</Link>. Strong passwords and quick
                                      offboarding protect every market.
                                    </p>
                                  </div>
                                </div>
                                <div className="col-md-6">
                                  <div className="support-topic-tile">
                                    <h6>
                                      <i className="mdi mdi-alert-circle-outline" />
                                      Technical Support
                                    </h6>
                                    <p className="text-muted small mb-0">
                                      Email{" "}
                                      <a href="mailto:contact@vyfoo.com">contact@vyfoo.com</a> with your restaurant name, country, a description of the issue,
                                      and your{" "}
                                      <strong>local timezone</strong> so we can review server logs.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="support-notice mb-0" role="status">
                        <strong className="d-block">Service Notice</strong>
                        <p className="mb-0 mt-2">
                          Scheduled maintenance will be announced by email and in{" "}
                          <Link to="/updates">System Updates</Link>.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <LoadingModal isLoading={isLoading} />
          <Notification />
        </>
      )}
    </>
  );
};

export default Support;
